/**
 * Shows connector ownership for a workspace-paired Vault.
 *
 * cm-coding: Vault users need to know that live files arrive from connectors
 * configured in the paired workspace. Show the workspace link and total feed
 * count here, but leave provider controls on the Connectors page; duplicating
 * them in Vault would create two places to edit the same import settings.
 */
import { ArrowRight, Plug } from 'lucide-react'

export interface VaultConnectorSummaryProps {
  connectorCount: number
  workspaceName: string
  onManage?: () => void
}

export function VaultConnectorSummary({
  connectorCount,
  workspaceName,
  onManage,
}: VaultConnectorSummaryProps) {
  return (
    <div
      className="flex flex-wrap items-center gap-2.5 rounded-[10px] border border-slate-100 bg-slate-50 px-3.5 py-2.5 text-[12.5px] text-slate-500"
      data-testid="vault-connector-summary"
    >
      <Plug className="h-3.5 w-3.5 shrink-0" />
      <span>
        {connectorCount} {connectorCount === 1 ? 'connector delivers' : 'connectors deliver'} into this Vault from workspace{' '}
        <strong className="font-semibold text-slate-700">{workspaceName}</strong>.
      </span>
      {onManage && (
        <button
          type="button"
          className="ml-auto inline-flex items-center gap-1.5 font-semibold text-slate-800 hover:text-slate-950"
          onClick={onManage}
        >
          Manage in Connectors
          <ArrowRight className="h-3 w-3" />
        </button>
      )}
    </div>
  )
}
